// src/app/services/token-storage.service.ts
import { Injectable } from '@angular/core';

// Persist token & user with an inactivity expiration policy (2h). On refresh,
// session is restored only if last activity is within the allowed window.
@Injectable({ providedIn: 'root' })
export class TokenStorageService {
  private _token: string | null = null;
  private _user: any = null;
  private static readonly TOKEN_KEY = 'auth-token';
  private static readonly USER_KEY = 'auth-user';
  private static readonly LAST_ACTIVITY_KEY = 'auth-last-activity';
  private static readonly MAX_IDLE_MS = 2 * 60 * 60 * 1000; // 2 hours

  constructor() {
    try {
      const token = localStorage.getItem(TokenStorageService.TOKEN_KEY);
      const userRaw = localStorage.getItem(TokenStorageService.USER_KEY);
      const lastActivityStr = localStorage.getItem(TokenStorageService.LAST_ACTIVITY_KEY);
      const lastActivity = lastActivityStr ? Number(lastActivityStr) : 0;
      const now = Date.now();
      if (token && userRaw && lastActivity && (now - lastActivity) < TokenStorageService.MAX_IDLE_MS) {
        this._token = token;
        try { this._user = JSON.parse(userRaw); } catch { this._user = null; }
        // touch activity on load
        this.updateActivity();
      } else {
        this.signOut();
      }
    } catch {
      this.signOut();
    }
  }

  signOut(): void {
    this._token = null;
    this._user = null;
    try {
      localStorage.removeItem(TokenStorageService.TOKEN_KEY);
      localStorage.removeItem(TokenStorageService.USER_KEY);
      localStorage.removeItem(TokenStorageService.LAST_ACTIVITY_KEY);
    } catch {}
  }

  saveToken(token: string): void {
    this._token = token;
    try { localStorage.setItem(TokenStorageService.TOKEN_KEY, token); } catch {}
    this.updateActivity();
  }

  getToken(): string | null {
    return this._token;
  }

  saveUser(user: any): void {
    this._user = user;
    try { localStorage.setItem(TokenStorageService.USER_KEY, JSON.stringify(user)); } catch {}
    this.updateActivity();
  }

  getUser(): any {
    return this._user || {};
  }

  updateActivity(): void {
    try { localStorage.setItem(TokenStorageService.LAST_ACTIVITY_KEY, Date.now().toString()); } catch {}
  }

  isExpired(): boolean {
    try {
      const ts = Number(localStorage.getItem(TokenStorageService.LAST_ACTIVITY_KEY) || '0');
      if (!ts) return true;
      return (Date.now() - ts) >= TokenStorageService.MAX_IDLE_MS;
    } catch { return true; }
  }
}
